/**
 * What the surfaces call the list an exam publishes in advance.
 *
 * The column holds two shapes and both are served. Exams authored under
 * ADR-0014 publish **example findings** -- defects already found in this
 * artefact, in the form a participant writes in -- and the exams published
 * before it publish a list of **risks**, which are classes of error. They are
 * different objects and the copy above them cannot be the same sentence: one
 * says "here is the shape, and these three are taken", the other says "here are
 * sixteen things that might be wrong with anything of this kind".
 *
 * Shared because four surfaces state it -- the public exam tab, the participant
 * brief, the review room, and the assessment card -- and a second copy of the
 * wording is a second thing to drift.
 */

/** One entry as the API normalises it. Structural, so the tRPC type flows in. */
export interface PublishedEntryView {
  ref: string;
  kind: "EXAMPLE" | "RISK";
  heading: string;
  location: string | null;
  fields: { label: string; value: string }[];
}

/**
 * The shape of a list, from its first entry.
 *
 * An empty list reads as EXAMPLE, which is what an exam authored today has
 * before its first entry and the shape any copy written now should assume.
 */
export function publishedListShape(
  entries: readonly { kind: "EXAMPLE" | "RISK" }[],
): "EXAMPLE" | "RISK" {
  return entries[0]?.kind ?? "EXAMPLE";
}

export function publishedListNoun(
  entries: readonly { kind: "EXAMPLE" | "RISK" }[],
): string {
  const plural = entries.length === 1 ? "" : "s";
  return publishedListShape(entries) === "EXAMPLE"
    ? `example finding${plural}`
    : `risk${plural} already identified`;
}

/** The section heading: "5 example findings", "16 risks already identified". */
export function publishedListTitle(
  entries: readonly { kind: "EXAMPLE" | "RISK" }[],
): string {
  return `${String(entries.length)} ${publishedListNoun(entries)}`;
}

/** The line under it, which is where the "and they score nothing" goes. */
export function publishedListSub(
  entries: readonly { kind: "EXAMPLE" | "RISK" }[],
): string {
  return publishedListShape(entries) === "EXAMPLE"
    ? "Defects already found in this artefact, in the form you write in. Finding one again counts for nothing"
    : "Written before anybody sat this. Repeating one counts for nothing";
}

/**
 * The scoring rule, which states the same fact as a rule rather than a heading.
 *
 * Returned as the pair `scoringRules` uses so the two cannot disagree about how
 * many entries there are or what they are called.
 */
export function publishedListRule(
  entries: readonly { kind: "EXAMPLE" | "RISK" }[],
): [string, string] {
  const count = String(entries.length);
  return publishedListShape(entries) === "EXAMPLE"
    ? [
        "Finding one of the published examples again counts for nothing",
        `All ${count} are shown below, written in the same boxes you fill in. A finding that names the same defect is refused, and the test is whether fixing one would also fix the other rather than whether the wording matches. They are published rather than held back so that recall is worth nothing.`,
      ]
    : [
        "Repeating a published risk counts for nothing",
        `All ${count} are published below. A finding that restates one is refused, which is why the list is public rather than held back.`,
      ];
}
